
import { useState, useMemo, useCallback } from "react";
import { useQuery as useConvexQuery, useAction } from "convex/react";
import { api } from "../../api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { AppIcon } from "./AppIcon";
import { Search, Package, Smartphone, RefreshCw, AlertCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface DeviceApp {
  bundleId: string;
  name?: string;
  trackId?: string;
  iconUrl?: string;
  version?: string;
  status: "allowed" | "blocked" | "unknown";
}

interface DeviceAppsInstalledContentProps {
  deviceId: string;
}

const getStatusBadge = (status: "allowed" | "blocked" | "unknown") => {
  switch (status) {
    case "allowed":
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Allowed</Badge>;
    case "blocked":
      return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Blocked</Badge>;
    case "unknown":
    default:
      return <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">Unknown</Badge>;
  }
};

export const DeviceAppsInstalledContent = ({ deviceId }: DeviceAppsInstalledContentProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [installedApps, setInstalledApps] = useState<DeviceApp[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // First get device details to access the serial number
  const deviceData = useConvexQuery(api.devices.get.details, 
    deviceId ? { device: deviceId as any } : "skip"
  );

  const deviceSerial = deviceData?.device?.serial;

  // Use action to fetch apps with status
  const fetchAppsAction = useAction(api.apps.list.allOnDeviceWithStatus);

  const fetchApps = useCallback(async () => {
    if (!deviceSerial) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await fetchAppsAction({ serial: deviceSerial });
      setInstalledApps(result || []);
    } catch (err) {
      console.error('Error fetching apps with status:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch installed apps');
      setInstalledApps([]);
    } finally {
      setIsLoading(false);
    }
  }, [deviceSerial, fetchAppsAction]);

  // Fetch apps when serial is available
  useState(() => {
    if (deviceSerial) {
      fetchApps();
    }
  });

  // Refetch when serial changes
  useMemo(() => {
    if (deviceSerial) {
      fetchApps();
    }
  }, [deviceSerial, fetchApps]);

  // Filter apps based on search term
  const filteredApps = useMemo(() => {
    if (!installedApps) return [];
    
    return installedApps.filter((app: DeviceApp) =>
      (app.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.bundleId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.status || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [installedApps, searchTerm]);

  if (isLoading || !deviceSerial) {
    return (
      <div className="space-y-6 h-full flex flex-col">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Installed Apps</h3>
          <p className="text-muted-foreground">Loading installed apps...</p>
        </div>
        
        <Card className="flex-1">
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={`loading-${i}`} className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-muted rounded"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/3"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 h-full flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Installed Apps</h3>
            <p className="text-muted-foreground">Apps currently installed on this device</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchApps} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Retry
          </Button>
        </div>
        
        <Card className="flex-1 flex items-center justify-center">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">Failed to Load Apps</h4>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={fetchApps}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!installedApps || installedApps.length === 0) {
    return (
      <div className="space-y-6 h-full flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Installed Apps</h3>
            <p className="text-muted-foreground">Apps currently installed on this device</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchApps} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        <Card className="flex-1 flex items-center justify-center">
          <CardContent className="p-12 text-center">
            <Smartphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Installed Apps Found</h4>
            <p className="text-muted-foreground">
              No apps are currently installed on this device, or the device hasn't synced recently.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Installed Apps</h3>
          <p className="text-muted-foreground">
            Apps currently installed on this device ({installedApps.length} total)
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchApps} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search installed apps..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Apps Table */}
      <Card className="flex-1 flex flex-col">
        <CardContent className="p-0 flex-1 flex flex-col">
          <ScrollArea className="flex-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>App Name</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8">
                      <div className="flex flex-col items-center">
                        <Package className="w-8 h-8 text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">
                          {searchTerm ? "No apps match your search" : "No apps found"}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApps.map((app: DeviceApp) => (
                    <TableRow key={app.bundleId} className="hover:bg-muted/50">
                      <TableCell>
                        <AppIcon 
                          app_icon_url={app.iconUrl} 
                          app_name={app.name} 
                          size="sm" 
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">
                          {app.name || app.bundleId}
                        </div>
                        {app.name && (
                          <div className="text-xs text-muted-foreground">
                            {app.bundleId}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(app.status)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Results count */}
      {searchTerm && (
        <div className="text-sm text-muted-foreground text-center">
          Showing {filteredApps.length} of {installedApps.length} apps
        </div>
      )}
    </div>
  );
};
